import { Survey } from "../models/survey.model";
import { localized, localizedUI } from "./SwitchLanguage";
import { edjsParser } from "./SurveyAppView";
import { useCallback, useEffect, useRef, useState } from "react";
import { getOneSurveyEventSubmitById } from "../actions/survey.event.action";
import { SurveyEventSubmit } from "../models/survey.event.model";
import { Coupon } from "../models/coupon.model";
import moment from "moment";

import { downloadScreenshot } from "../lib/helper";
import LoadingButton from "../components/LoadingButton";
import { Stack } from "@mui/material";
import { debounce } from "lodash";

export default function SurveyEventCompleteView(props: {
  survey: Survey;
  language: string;
  eventId?: string;
  onComplete: () => Promise<void>;
}) {
  const { survey, language, eventId } = props;
  const submitId = localStorage.getItem("surveyEventSubmitId");
  const [submit, setSubmit] = useState<SurveyEventSubmit>();
  const showCouponComplete = survey.couponComplete && submit?.coupon;
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(false);

  const replacePlaceHolders = (html: string, coupon?: Coupon): string => {
    if (!showCouponComplete) {
      return html;
    }
    return html
      .replaceAll("{{coupon.code}}", coupon?.code || "")
      .replaceAll("{{coupon.title}}", coupon?.title || "")
      .replaceAll("{{coupon.description}}", coupon?.description || "")
      .replaceAll("{{coupon.expireDate}}", coupon?.expireDate ? moment(coupon.expireDate).format("YYYY-MM-DD") : "");
  };

  const onClickComplete = useCallback(
    debounce(async () => {
      try {
        await props.onComplete();
        if (eventId && submitId) {
          const res = await getOneSurveyEventSubmitById(eventId, submitId);
          setSubmit(res);
        }
        setCompleted(true);
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    }, 500),
    [props.onComplete, eventId, submitId]
  );

  useEffect(() => {
    setLoading(true);
    onClickComplete();
  }, [eventId, submitId]);

  return (
    <div style={{ flex: 1, display: "flex" }}>
      <Stack justifyContent={"space-between"} width={"100%"}>
        <div
          className="html-editor"
          style={{ flex: 1 }}
          dangerouslySetInnerHTML={
            showCouponComplete
              ? {
                  __html: survey.couponComplete
                    ? replacePlaceHolders(
                        edjsParser.parse(localized(language, survey, "complete", survey.couponComplete)).join(""),
                        submit?.coupon
                      )
                    : "",
                }
              : {
                  __html: survey.complete
                    ? edjsParser.parse(localized(language, survey, "complete", survey.complete)).join("")
                    : "",
                }
          }
        />
        {completed && showCouponComplete ? (
          <LoadingButton
            onClick={() => {
              const appView = document.getElementById("survey-app-view");
              downloadScreenshot({
                fileName: "coupon",
                target: appView || document.body,
              });
            }}
            className="width-100p"
            variant="contained"
            data-testid="download-coupon"
            disableRipple
          >
            Download screenshot
          </LoadingButton>
        ) : (
          <LoadingButton
            onClick={!completed ? onClickComplete : undefined}
            className="width-100p"
            variant="contained"
            disabled={completed}
            loading={loading}
            key={loading ? "loading" : "not-loading"}
            disableRipple
            data-testid="complete"
          >
            {localizedUI(language, completed ? "completed" : "complete")}
          </LoadingButton>
        )}
      </Stack>
    </div>
  );
}
